
import { Dealer } from '@/types/dealer';
import { fetchRecommendedDealersData } from './fetchData';
import { 
  setPrefetchedDealers, 
  getPrefetchedDealersFromCache 
} from './cache';
import { debugLog, debugError } from '@/utils/debugLogger';

let prefetchInProgress = false;

/**
 * Prefetch recommended dealers data for given metal type
 * @param metalType The metal type to prefetch data for
 * @param forceRefresh Whether to force a refresh of the data
 * @returns A promise that resolves when prefetching is complete
 */
export const prefetchRecommendedDealers = async (
  metalType: string = 'ORO',
  forceRefresh: boolean = false
): Promise<void> => {
  // Prevent multiple concurrent prefetches
  if (prefetchInProgress) {
    debugLog('cache', "Prefetch already in progress, skipping", undefined, 'prefetch');
    return;
  }
  
  try {
    debugLog('cache', "Starting prefetch for dealers", { metalType, forceRefresh }, 'prefetch');
    prefetchInProgress = true;
    
    // Skip prefetch if we already have data and not forcing refresh
    if (!forceRefresh && hasPrefetchedDealers()) {
      debugLog('cache', "Dealers already prefetched, skipping", undefined, 'prefetch');
      return;
    }
    
    // Fetch fresh data
    const dealers = await fetchRecommendedDealersData(metalType, null, false, forceRefresh);
    
    if (dealers && dealers.length > 0) {
      debugLog('cache', "Prefetched dealers", { count: dealers.length, metalType }, 'prefetch');
      setPrefetchedDealers(metalType, dealers);
      
      // Log each dealer's rating
      dealers.forEach(dealer => {
        debugLog('dealer', "Prefetched dealer with rating", { name: dealer.name, rating: dealer.rating }, 'prefetch');
      });
    } else {
      debugLog('cache', "No dealers to prefetch", undefined, 'prefetch');
    }
  } catch (error) {
    debugError('cache', "Error during dealers prefetch", error, 'prefetch');
  } finally {
    prefetchInProgress = false;
  }
};

/**
 * Check if dealers have been prefetched
 * @returns True if dealers have been prefetched, false otherwise
 */
export const hasPrefetchedDealers = (): boolean => {
  const prefetchedDealers = getPrefetchedDealersFromCache();
  const hasDealers = Boolean(prefetchedDealers && prefetchedDealers.length > 0);
  
  debugLog('cache', "Prefetched dealers check", { available: hasDealers }, 'prefetch');
  
  if (hasDealers) {
    debugLog('cache', "Prefetched dealers available", { count: prefetchedDealers?.length }, 'prefetch');
  }
  
  return hasDealers;
};
